import axios from "axios";
import { StorageManager } from "datastake-daf/src/helpers/StorageManager";
import { getToken, isProxy } from 'datastake-daf/src/helpers/Token';
import ErrorService from './ErrorService';

const cleanJSON = (json) => {
  for (let key in json) {
    if (json[key] === undefined) {
      json[key] = null;
    } /*else if (typeof json[key] === 'object')
      json[key] = cleanJSON(json[key]);
      */
  }
  return json;
}

export const getProject = () => {
  if (isProxy()) {
    return StorageManager.get('proxy_project', 'myOwnData');
  }

  return StorageManager.get('project', 'myOwnData');
}

export const getActiveModule = () => {
  const [, authApp] = window.location.pathname.split(/^\/(tif|tazama|admin|cadd|pme)\/.*$/);
  const [, appApp] = window.location.pathname.split(/^\/app\/(tif|tazama|admin|cadd|pme)\/.*$/);
  const [, dashboardApp] = window.location.pathname.split(/^\/app\/(tif|tazama|admin|cadd|pme)$/);
  const mod = authApp || appApp || dashboardApp;
  return mod;
};

export const getAppFromModule = (mod) => {
  switch (mod) {
    case 'tif':
      return 'tif';
    case 'tazama':
      return 'tazama';
    case 'admin':
      return 'admin';
    default:
      return 'mmt';
  }
}

export const getApplication = () => {
  const app = getActiveModule();

  if (process.env.REACT_APP_APP && app !== 'admin') {
    return process.env.REACT_APP_APP;
  }

  switch (app) {
    case 'tif':
      return 'tif';
    case 'tazama':
      return 'tazama';
    case 'admin':
      return 'admin';
    default:
      return 'mmt';
  }
};

export const isDaf = () => {
  const app = getApplication();
  return !['mmt', 'admin'].includes(app);
}

export const getMainApiUrl = () => {
  return process.env.REACT_APP_API_GATEWAY_ENDPOINT;
}

export const getApiBaseUrl = (options) => {
  if (options?.isStore) {
    return process.env.REACT_APP_STORE_URL;
  }

  if (options?.isDaf) {
    return process.env.REACT_APP_DAF_API_URL;
  }

  return isDaf() ? process.env.REACT_APP_DAF_API_URL : getMainApiUrl();
}

export const getAppHeader = () => ({
  Application: getApplication(),
  Language: StorageManager.get('datastakeLng'),
});

class BaseService {
  constructor() {
    this.cancelToken = {};
    this.token = getToken();
    const sourceId = StorageManager.get('source', undefined);

    const headers = {
      Authorization: `Bearer ${getToken()}`,
      Language: StorageManager.get('datastakeLng'),
      Project: getProject(),
      'ngrok-skip-browser-warning': true,
      ...getAppHeader(),
    }

    if (sourceId && sourceId !== 'overview') {
      headers.Source = sourceId;
    }
    this.api = axios.create({
      baseURL: getApiBaseUrl(),
      headers,
    });

    this.api.interceptors.response.use(
      (response) => {
        return response;
      },
      (error) => {
        if (error.response) {
          const { status, statusText, data: { errors, message } } = error?.response || { data: {} };
          // Do something with response error
          if (error.response && error.response.status === 401) {
            const token = StorageManager.get('token');
            if (token) {
              StorageManager.clearOne("token");
              window.location.href = "/";
            }
          }
          if (status >= 400 && status <= 500) {
            ErrorService.handle({ status, statusText: message || statusText, data: errors || [] });
          }
          return Promise.reject(error);
        }
      }
    );
    this.api.interceptors.request.use(
      function (config) {
        if (['post', 'put', 'patch'].includes(config.method)) {
          config.data = cleanJSON(config.data);
        }
        return config;
      },
      function (error) {
        return Promise.reject(error);
      }
    );
  }
  reloadAxios() {
    const options = {
      baseURL: getApiBaseUrl(),
    };
    const token = getToken();
    const sourceId = StorageManager.get('source', undefined);
    this.token = token;
    if (token) {
      options.headers = {
        Authorization: `Bearer ${getToken()}`,
        Language: StorageManager.get('datastakeLng'),
        Project: getProject(),
        'ngrok-skip-browser-warning': true,
        ...getAppHeader(),
      };
      if (sourceId && sourceId !== 'overview') {
        options.headers.Source = sourceId;
      }
    }
    this.api = axios.create(options);
    this.api.interceptors.response.use(
      (response) => {
        return response;
      },
      (error) => {
        const { status, statusText, data: { errors, message } } = error?.response || { data: {} };
        // Do something with response error
        if (error.response && error.response.status === 401) {
          const token = StorageManager.get('token');
          if (token) {
            StorageManager.clearOne("token");
            window.location.href = "/";
          }
        }
        if (status >= 400 && status <= 500) {
          ErrorService.handle({ status, statusText: message || statusText, data: errors || [] });
        }
        return Promise.reject(error);
      }
    );
    this.api.interceptors.request.use(
      function (config) {
        if (['post', 'put', 'patch'].includes(config.method)) {
          config.data = cleanJSON(config.data);
        }
        return config;
      },
      function (error) {
        return Promise.reject(error);
      }
    );
  }

  async formatResponse(call) {
    const res = await call;
    const { data, status, statusText } = res || {};
    this.cancelToken = {};
    return { data, status, statusText };
  }

  apiPost(options) {
    //Check if there are any previous pending requests
    if (typeof this.cancelToken[options.url] != typeof undefined) {
      try {
        this.cancelToken[options.url].cancel("Operation canceled due to new request.")
      } catch (e) {
        console.log(e);
        return {};
      }
    }
    this.cancelToken[options.url] = axios.CancelToken.source()
    const token = getToken();
    if (token !== this.token || !token) {
      this.reloadAxios();
    }

    return this.formatResponse(this.api.post(options.url, options.data, {
      baseURL: getApiBaseUrl(options),
      cancelToken: this.cancelToken[options.url].token,
      headers: {
        Authorization: `Bearer ${getToken()}`,
        Language: StorageManager.get('datastakeLng'),
        Project: getProject(),
        ...(options?.headers || {}),
        ...getAppHeader(),
      }
    }));
  }

  apiGet(options) {
    const token = getToken();
    if (token !== this.token || !token) {
      this.reloadAxios();
    }
    const { url, ...config } = options;

    return this.formatResponse(this.api.get(url, {
      ...config,
      baseURL: getApiBaseUrl(options),
      headers: options.isStore ? {
        Authorization: `Bearer ${getToken()}`,
      } : {
        Authorization: `Bearer ${getToken()}`,
        Language: StorageManager.get('datastakeLng'),
        Project: getProject(),
        ...getAppHeader(),
        ...(config?.headers || {}),
      }
    }));
  }

  apiDelete(options) {
    const token = getToken();
    if (token !== this.token || !token) {
      this.reloadAxios();
    }
    const { url, ...config } = options;

    return this.formatResponse(this.api.delete(url, {
      ...config,
      baseURL: getApiBaseUrl(options),
      headers: {
        Authorization: `Bearer ${getToken()}`,
        Language: StorageManager.get('datastakeLng'),
        Project: getProject(),
        ...getAppHeader(),
        ...(config?.headers || {}),
      }
    }));
  }

  apiPut(options) {
    //Check if there are any previous pending requests
    if (typeof this.cancelToken[options.url] != typeof undefined) {
      try {
        this.cancelToken[options.url].cancel("Operation canceled due to new request.")
      } catch (e) {
        console.log(e);
        return {};
      }
    }
    this.cancelToken[options.url] = axios.CancelToken.source()
    const token = getToken();
    if (token !== this.token || !token) {
      this.reloadAxios();
    }

    return this.formatResponse(this.api.put(options.url, options.data, {
      cancelToken: this.cancelToken[options.url].token,
      baseURL: getApiBaseUrl(options),
      headers: {
        Authorization: `Bearer ${getToken()}`,
        Language: StorageManager.get('datastakeLng'),
        Project: getProject(),
        ...(options?.headers || {}),
        ...getAppHeader(),
      }
    }));
  }

  assignParamsToUrl(path, params) {
    let paramsPath = path;
    for (let param of path.match(/\{\w+\}/gm)) {
      const key = param.match(/\w+/)[0];
      paramsPath = paramsPath.replace(/\{\w+\}/, params[key]);
    }
    return paramsPath;
  }
}

export default BaseService;
